import {FormProvider, useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import {Alert, Button, Col, Row, Space} from "antd";
import {useGetBundleContext} from "../context/get-bundle";
import {OfferingFormCheckoutFormValues} from "../../../../components/offering-form/general/checkout-form/types";
import {offeringCheckoutFormValidationSchema} from "../../../../components/offering-form/general/checkout-form/validation-schema";
import {useEditBundleHook} from "../hooks/edit-bundle";
import {useNavigate} from "react-router-dom";
import {StyledServiceButtonWrapper} from "../../../../styles/onboarding/onboarding";
import {EditBundleFormValues} from "../types";
import {ServiceFormLayout} from "../../createService/service-form-layout";
import {SessionCheckoutFormFields} from "../../../../components/offering-form/session-bundle/session/checkout-form";
import {useDeleteBundleHook} from "../hooks/delete-bundle";
import {OfferingDeleteButton} from "../../../../components/offering-form/general/delete-button";
import {
  useCoverImagesWithFallback,
  mapDomainToEmbedded,
} from "../../../../features/media";
import {toSavedEditBundleFormValues} from "../saved-form-values";
import {IS_PWYW_ENABLED} from "../../../../services/config";

export function EditBundleCheckoutForm() {
  const navigate = useNavigate();
  const {
    state: {sessionData},
  } = useGetBundleContext();

  // Fetch cover images via v2 with fallback to embedded data
  const {coverImages} = useCoverImagesWithFallback({
    offeringType: "service", // bundles are service type
    offeringId: sessionData.id,
    embedded: sessionData.cover_images,
  });

  // Convert domain format to API format for payload
  const coverImagesForPayload = mapDomainToEmbedded(coverImages);
  const checkoutFormMethods = useForm<OfferingFormCheckoutFormValues>({
    resolver: yupResolver(offeringCheckoutFormValidationSchema),
    mode: "onChange",
    defaultValues: {
      bookingQuestions: sessionData.bookingQuestions
        ? sessionData.bookingQuestions.map((q) => {
            if (
              q.answer_type === "checkbox" ||
              q.answer_type === "dropdown" ||
              q.answer_type === "radio-button"
            ) {
              return {
                question: q.question,
                required: q.required,
                answerType: q.answer_type,
                options: q.options.map((o) => ({
                  optionLabel: o,
                })),
              };
            } else {
              return {
                question: q.question,
                required: q.required,
                answerType: q.answer_type,
              };
            }
          })
        : [],
    },
  });
  const {editBundle, isEditingBundle} = useEditBundleHook();
  const {deleteBundle} = useDeleteBundleHook(sessionData.id);
  function onSubmit(values: OfferingFormCheckoutFormValues) {
    // The update carries the whole bundle, so everything this tab does not edit goes back
    // exactly as saved: currencies, redirect, location and availability schedule.
    const payload: EditBundleFormValues = {
      ...toSavedEditBundleFormValues(sessionData, IS_PWYW_ENABLED),
      id: sessionData.id,
      cover_images: coverImagesForPayload,
      bookingQuestions: values.bookingQuestions,
    };
    editBundle(payload);
  }
  const isFormError = Object.keys(checkoutFormMethods.formState.errors).length > 0;
  const isBookingQuestionsError = checkoutFormMethods.formState.errors.bookingQuestions;
  return (
    <ServiceFormLayout>
      <FormProvider {...checkoutFormMethods}>
        <form onSubmit={checkoutFormMethods.handleSubmit(onSubmit)} autoComplete="off">
          <Space size={16} direction="vertical" style={{display: "flex"}}>
            <SessionCheckoutFormFields
              isBookingQuestionsError={!!isBookingQuestionsError}
            />
            {isFormError && (
              <Alert
                message="Please fill in the required fields above, outlined in red"
                type="warning"
              />
            )}
            <Row gutter={[0, 16]}>
              <Col span={24} sm={12}>
                <StyledServiceButtonWrapper>
                  <Button
                    onClick={() => navigate("/calls")}
                    block
                    size="small"
                    type="default"
                  >
                    Back
                  </Button>
                  <Button
                    block
                    type="primary"
                    htmlType="submit"
                    size="small"
                    disabled={
                      Object.keys(checkoutFormMethods.formState.errors).length > 0 ||
                      Object.values(checkoutFormMethods.watch()).length < 1
                    }
                    loading={isEditingBundle}
                  >
                    Update bundle
                  </Button>
                  <OfferingDeleteButton
                    cancelText="Exit"
                    content="Are you sure you want to delete this bundle? This action cannot be undone."
                    okText="Delete bundle"
                    onOk={deleteBundle}
                    title="Confirm bundle deletion"
                  />
                </StyledServiceButtonWrapper>
              </Col>
            </Row>
          </Space>
        </form>
      </FormProvider>
    </ServiceFormLayout>
  );
}
